import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api.js";
import type {
  Category,
  FunkoDetails,
  GtPoppediaApplyResult,
  GtPoppediaSearchResponse,
  GtPoppediaSuggestionResponse,
  Item,
  Paginated,
} from "../types.js";

export interface ItemFilters {
  q: string;
  categoryId: string;
  condition: string;
  series: string;
  location: string;
  forSale: boolean;
  featured: boolean;
  needsValuation: boolean;
  /** Binary status: an item is either available or sold. */
  status: "available" | "sold" | "all" | "deleted";
  sort: "name" | "series" | "createdAt" | "updatedAt" | "currentValue" | "forSalePrice";
  order: "asc" | "desc";
  limit: number;
  offset: number;
}

export const DEFAULT_FILTERS: ItemFilters = {
  q: "",
  categoryId: "",
  condition: "",
  series: "",
  location: "",
  forSale: false,
  featured: false,
  needsValuation: false,
  // Available by default; switch to Sold or All to widen the list.
  status: "available",
  sort: "updatedAt",
  order: "desc",
  limit: 25,
  offset: 0,
};

export function toSearchParams(filters: ItemFilters): string {
  const params = new URLSearchParams();
  if (filters.q.trim()) params.set("q", filters.q.trim());
  if (filters.categoryId) params.set("categoryId", filters.categoryId);
  if (filters.condition) params.set("condition", filters.condition);
  if (filters.series.trim()) params.set("series", filters.series.trim());
  if (filters.location.trim()) params.set("location", filters.location.trim());
  if (filters.forSale) params.set("forSale", "true");
  if (filters.featured) params.set("featured", "true");
  if (filters.needsValuation) params.set("needsValuation", "true");
  if (filters.status === "sold") params.set("sold", "true");
  if (filters.status === "available") params.set("sold", "false");
  if (filters.status === "deleted") params.set("deletedOnly", "true");
  params.set("sort", filters.sort);
  params.set("order", filters.order);
  params.set("limit", String(filters.limit));
  params.set("offset", String(filters.offset));
  return params.toString();
}

export function useItems(filters: ItemFilters) {
  const search = toSearchParams(filters);
  return useQuery({
    queryKey: ["items", search],
    queryFn: () => api.get<Paginated<Item>>(`/items?${search}`),
    placeholderData: (previous) => previous,
  });
}

export interface ItemStats {
  total: number;
  available: number;
  sold: number;
  totalCost: number;
  estimatedValue: number;
}

export interface PriceHistoryEntry {
  id: string;
  price: string;
  source: string;
  recordedAt: string;
}

/** Dashboard totals over the same filtered set the table is showing. */
export function useItemStats(filters: ItemFilters) {
  const search = toSearchParams(filters);
  return useQuery({
    queryKey: ["item-stats", search],
    queryFn: () => api.get<ItemStats>(`/items/stats?${search}`),
    placeholderData: (previous) => previous,
  });
}

/** Valuation history for one item, newest first. */
export function usePriceHistory(itemId: string | undefined) {
  return useQuery({
    queryKey: ["price-history", itemId],
    queryFn: () =>
      api.get<{ data: PriceHistoryEntry[] }>(`/items/${itemId}/price-history`).then((r) => r.data),
    enabled: Boolean(itemId),
  });
}

export interface ItemSuggestions { license:string[]; series:string[]; exclusiveTo:string[]; location:string[] }
export function useItemSuggestions(prefix = "") {
  const [query, setQuery] = useState(prefix);
  useEffect(() => { const timer = setTimeout(() => setQuery(prefix), 200); return () => clearTimeout(timer); }, [prefix]);
  return useQuery({ queryKey:["item-suggestions", query], queryFn:()=>api.get<{data:ItemSuggestions}>(`/items/suggestions?q=${encodeURIComponent(query)}`).then(r=>r.data), staleTime: 30_000 });
}

export function useFeatureSuggestions(prefix = "") {
  const [query, setQuery] = useState(prefix);
  useEffect(() => { const timer = setTimeout(() => setQuery(prefix), 150); return () => clearTimeout(timer); }, [prefix]);
  return useQuery({ queryKey: ["funko-features", query], queryFn: () => api.get<{ data: string[]; hasMore: boolean }>(`/items/features?q=${encodeURIComponent(query)}`), staleTime: 30_000 });
}
export function useCreateFeature() {
  const client = useQueryClient();
  return useMutation({ mutationFn: (name: string) => api.post<{ data: string }>("/items/features", { name }).then(result => result.data), onSuccess: () => { void client.invalidateQueries({ queryKey: ["funko-features"] }); } });
}

export function useCategories() {
  return useQuery({
    queryKey: ["categories"],
    queryFn: () => api.get<{ data: Category[] }>("/categories").then((r) => r.data),
  });
}

/** Name plus optional explicit slug and parent — what the category forms send. */
export interface CategoryInput {
  name: string;
  slug?: string;
  parentId?: string | null;
  whatnotCategoryId?: number | null;
  whatnotCategoryType?: string | null;
  whatnotTaxonomyVersion?: string | null;
  whatnotConditionAttributeId?: string | null;
}

export function useCreateCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CategoryInput) =>
      api.post<{ data: Category }>("/categories", input).then((response) => response.data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["categories"] });
    },
  });
}

export function useUpdateCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: CategoryInput }) =>
      api.patch<{ data: Category }>(`/categories/${id}`, patch).then((response) => response.data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["categories"] });
    },
  });
}

export function useDeleteCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/categories/${id}`),
    onSuccess: () => {
      // Items carry a categoryId, and their filter chips come from this list.
      void queryClient.invalidateQueries({ queryKey: ["categories"] });
      void queryClient.invalidateQueries({ queryKey: ["inventory"] });
    },
  });
}

export function useFunkoDetails(itemId: string | undefined) {
  return useQuery({
    queryKey: ["funko", itemId],
    queryFn: () => api.get<{ data: FunkoDetails | null }>(`/items/${itemId}/funko`).then((r) => r.data),
    enabled: Boolean(itemId),
  });
}

export function useCreateItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      api.post<{ data: Item }>("/items", payload).then((r) => r.data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["items"] });
      void queryClient.invalidateQueries({ queryKey: ["item-suggestions"] });
      void queryClient.invalidateQueries({ queryKey: ["funko-features"] });
    },
  });
}

export function useUpdateItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, unknown> }) =>
      api.patch<{ data: Item }>(`/items/${id}`, payload).then((r) => r.data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["items"] });
      void queryClient.invalidateQueries({ queryKey: ["item-suggestions"] });
      void queryClient.invalidateQueries({ queryKey: ["funko-features"] });
    },
  });
}

export function useDeleteItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/items/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["items"] });
      void queryClient.invalidateQueries({ queryKey: ["item-suggestions"] });
      void queryClient.invalidateQueries({ queryKey: ["funko-features"] });
    },
  });
}

export function useRestoreItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ data: Item }>(`/items/${id}/restore`, {}).then((r) => r.data),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["items"] }),
  });
}

export function useBulkRestore() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) =>
      api.post<{ data: { restored: number; notFound: number } }>("/items/bulk-restore", { ids }).then((r) => r.data),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["items"] }),
  });
}

export function useBulkUpdate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, patch }: { ids: string[]; patch: Record<string, unknown> }) =>
      api
        .post<{ data: { updated: number; unchanged: number; notFound: number } }>(
          "/items/bulk-update",
          { ids, patch },
        )
        .then((response) => response.data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["items"] });
      void queryClient.invalidateQueries({ queryKey: ["item-suggestions"] });
      void queryClient.invalidateQueries({ queryKey: ["funko-features"] });
    },
  });
}

export function useBulkDelete() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) =>
      api
        .post<{ data: { deleted: number; notFound: number } }>("/items/bulk-delete", { ids })
        .then((response) => response.data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["items"] });
      void queryClient.invalidateQueries({ queryKey: ["item-suggestions"] });
      void queryClient.invalidateQueries({ queryKey: ["funko-features"] });
    },
  });
}

export function useSaveFunko() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, payload }: { itemId: string; payload: Record<string, unknown> }) =>
      api.patch<{ data: FunkoDetails }>(`/items/${itemId}/funko`, payload).then((r) => r.data),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ["funko", variables.itemId] });
    },
  });
}

export function useGtPoppediaSearch(q: string, enabled: boolean) {
  return useQuery({
    queryKey: ["gt-poppedia", "search", q],
    queryFn: () => api.get<GtPoppediaSearchResponse>(`/gt-poppedia/search?q=${encodeURIComponent(q)}`),
    enabled: enabled && q.trim().length > 0,
  });
}

export function useGtPoppediaSuggestion(popUuid: string | null) {
  return useQuery({
    queryKey: ["gt-poppedia", "suggestion", popUuid],
    queryFn: () =>
      api.get<GtPoppediaSuggestionResponse>(`/gt-poppedia/pop/${popUuid}/suggestion`).then((r) => r.data),
    enabled: Boolean(popUuid),
  });
}

export function useApplyGtPoppedia() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, popUuid }: { itemId: string; popUuid: string }) =>
      api.post<{ data: GtPoppediaApplyResult }>(`/gt-poppedia/apply/${popUuid}/items/${itemId}`).then((r) => r.data),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ["items"] });
      void queryClient.invalidateQueries({ queryKey: ["item-suggestions"] });
      void queryClient.invalidateQueries({ queryKey: ["funko-features"] });
      void queryClient.invalidateQueries({ queryKey: ["item", variables.itemId] });
      void queryClient.invalidateQueries({ queryKey: ["funko", variables.itemId] });
    },
  });
}

export function useRefreshGtPoppediaValuation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) =>
      api.post<{ data: GtPoppediaApplyResult }>(`/gt-poppedia/refresh/${itemId}`).then((r) => r.data),
    onSuccess: (_data, itemId) => {
      void queryClient.invalidateQueries({ queryKey: ["items"] });
      void queryClient.invalidateQueries({ queryKey: ["item-suggestions"] });
      void queryClient.invalidateQueries({ queryKey: ["funko-features"] });
      void queryClient.invalidateQueries({ queryKey: ["item", itemId] });
      void queryClient.invalidateQueries({ queryKey: ["funko", itemId] });
    },
  });
}

